"use client";

import clsx from "clsx";
import Link from "next/link";
import { MarketingHeroEmailCta } from "@/components/redesign/MarketingHeroEmailCta";
import { useLocalePath } from "@/hooks/useLocalePath";

export type MarketingCtaSectionProps = {
  title: string;
  subtitle: string;
  emailPlaceholder: string;
  ctaLabel: string;
  personalEmailWarning: string;
  continueAnywayLabel: string;
  getStartedPrompt?: string;
  getStartedLinkLabel?: string;
  /** Plain footnote rendered instead of the "get started" link prompt. */
  footnote?: string;
  /** Identifies where the form was submitted from for analytics. */
  source?: string;
  className?: string;
};

export function MarketingCtaSection({
  title,
  subtitle,
  emailPlaceholder,
  ctaLabel,
  personalEmailWarning,
  continueAnywayLabel,
  getStartedPrompt,
  getStartedLinkLabel,
  footnote,
  source = "homepage_cta",
  className,
}: Readonly<MarketingCtaSectionProps>) {
  const lp = useLocalePath();

  return (
    <section className={clsx("w-full px-4 pb-16 md:pb-24", className)}>
      <div className="mx-auto w-full max-w-6xl">
        <div className="rounded-3xl border border-black/10 bg-marketing-voice-card px-6 py-16 md:px-10 md:py-20">
          <div className="mx-auto flex max-w-xl flex-col items-center text-center">
            <h2 className="font-monument text-[2rem] font-normal leading-[1.12] tracking-tight text-black md:text-[2.5rem]">
              {title}
            </h2>
            <p className="mt-4 font-monument text-base leading-relaxed text-gail-gray md:text-lg">
              {subtitle}
            </p>

            <div className="mt-8 flex w-full justify-center">
              <MarketingHeroEmailCta
                emailPlaceholder={emailPlaceholder}
                ctaLabel={ctaLabel}
                personalEmailWarning={personalEmailWarning}
                continueAnywayLabel={continueAnywayLabel}
                source={source}
                placement="cta_email"
              />
            </div>

            {footnote ? (
              <p className="mt-5 font-monument text-sm text-gail-gray">
                {footnote}
              </p>
            ) : getStartedPrompt && getStartedLinkLabel ? (
              <p className="mt-5 flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 font-monument text-sm text-gail-gray">
                <span>{getStartedPrompt}</span>
                <Link
                  href={lp("/signup")}
                  className="whitespace-nowrap text-gail-primary underline-offset-2 transition-colors hover:underline"
                >
                  {getStartedLinkLabel}
                </Link>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
